/**
 * @file src/api/invoice.api.js
 * @description All invoice-related API calls.
 *
 * Covers:
 *  - Invoice CRUD (create, read, update draft)
 *  - Invoice lifecycle (issue, clear, save draft)
 *  - PDF and XML generation / download
 *  - Batch upload operations
 *  - Invoice processing progress
 *  - Audit trail
 *
 * Rules:
 *  - No try/catch — ApiError propagates from lib/axios.js interceptor
 *  - No console.log
 *  - No React, no hooks, no state
 *  - DOM operations (blob download) isolated to dedicated helpers at
 *    the bottom — keeps API functions pure
 */

import { apiClient } from "lib/axios";

// ---------------------------------------------------------------------------
// Invoice lifecycle
// ---------------------------------------------------------------------------

/**
 * Create a new invoice.
 * On ZATCA validation failure the interceptor throws ApiError with
 * validationErrors[], uuid, invoiceNumber, invoiceCounterValue populated.
 *
 * @param {object} data - Invoice payload
 * @returns {Promise<object>} Created invoice
 */
export const createInvoice = async (data) => {
  const { data: responseData } = await apiClient.post("/invoices/create", data);
  return responseData;
};

/**
 * Issue (submit to ZATCA) a previously created invoice.
 * @param {string} invoiceId
 * @returns {Promise<object>}
 */
export const issueInvoice = async (invoiceId) => {
  const { data } = await apiClient.post(`/invoices/${invoiceId}/issue`, {});
  return data;
};

/**
 * Clear an invoice through ZATCA.
 * @param {string} invoiceId
 * @returns {Promise<object>}
 */
export const clearInvoice = async (invoiceId) => {
  const { data } = await apiClient.post(`/invoices/${invoiceId}/issue`, {});
  return data;
};

/**
 * Save invoice as draft.
 * @param {object} data - Invoice payload
 * @returns {Promise<object>} Draft invoice
 */
export const saveDraftInvoice = async (data) => {
  const { data: responseData } = await apiClient.post("/invoices/draft", data);
  return responseData;
};

/**
 * Update an existing draft invoice.
 * @param {string} invoiceId
 * @param {object} data - Partial or full invoice fields
 * @returns {Promise<object>} Updated draft
 */
export const updateDraftInvoice = async (invoiceId, data) => {
  const { data: responseData } = await apiClient.put(
    `/invoices/${invoiceId}/draft`,
    data,
  );
  return responseData;
};
/**
 * Fetch open invoices for a client so payments can be allocated.
 * @param {string} clientId
 * @param {object} params
 * @returns {Promise<object>}
 */
export const getOpenInvoicesForClient = async (clientId, params = {}) => {
  const { data } = await apiClient.get(`/invoices/open/client/${clientId}`, {
    params,
  });
  return data;
};

/**
 * Void an issued invoice before ZATCA reporting/clearance.
 * @param {string} invoiceId
 * @param {{ reason?: string }} body
 */
export const voidInvoice = async (invoiceId, body = {}) => {
  const { data } = await apiClient.post(`/invoices/${invoiceId}/void`, body);
  return data;
};
// ---------------------------------------------------------------------------
// Invoice reads
// ---------------------------------------------------------------------------

/**
 * Fetch the full paginated invoice list.
 * @returns {Promise<object>} Invoice list response
 */
export const getInvoices = async (params = {}) => {
  const { data } = await apiClient.get("/invoices/list", {
    params,
  });
  return data;
};

/**
 * Fetch a single invoice by ID.
 * @param {string} invoiceId
 * @returns {Promise<object>} Invoice detail
 */
export const getInvoiceById = async (invoiceId) => {
  const { data } = await apiClient.get(`/invoices/${invoiceId}`);
  return data;
};

/**
 * Fetch an invoice by its file name.
 * Used in the invoice processing queue.
 * @param {string} name - Invoice file name
 * @returns {Promise<object>}
 */
export const getInvoiceByName = async (name) => {
  const { data } = await apiClient.get(`/invoices/by-name/${name}`);
  return data;
};

/**
 * Fetch the audit trail for an invoice.
 * @param {string} invoiceId
 * @returns {Promise<object>}
 */
export const getInvoiceAuditTrail = async (invoiceId) => {
  const { data } = await apiClient.get(`/invoices/${invoiceId}/audit-trail`);
  return data;
};

// ---------------------------------------------------------------------------
// PDF
// ---------------------------------------------------------------------------

/**
 * Generate and return a PDF blob for in-browser preview.
 * Does NOT save the file to disk.
 *
 * @param {string} invoiceId
 * @param {string} templateId - Visual template ID
 * @returns {Promise<Blob>} PDF blob — pass to URL.createObjectURL for preview
 */
export const generateInvoicePdfBlob = async (invoiceId, templateId) => {
  const { data } = await apiClient.get(
    `/invoices/${invoiceId}/pdf?download=false&saveFile=false&templateId=${templateId}`,
    { responseType: "blob" },
  );
  return data;
};

/**
 * Download and save a PDF to the server, then return metadata.
 * @param {string} invoiceId
 * @returns {Promise<object>}
 */
export const downloadAndSaveInvoicePdf = async (invoiceId) => {
  const { data } = await apiClient.get(
    `/invoices/${invoiceId}/pdf?download=true&saveFile=true`,
  );
  return data;
};

// ---------------------------------------------------------------------------
// XML
// ---------------------------------------------------------------------------

/**
 * Download the XML for an invoice and trigger a browser file save.
 * Isolates all DOM side-effects here so callers stay clean.
 *
 * @param {string} invoiceId
 * @param {string} invoiceNumber - Used as the filename
 * @returns {Promise<void>}
 */
export const downloadInvoiceXml = async (invoiceId, invoiceNumber) => {
  const { data } = await apiClient.get(`/invoices/${invoiceId}/xml`, {
    responseType: "text",
    headers: { Accept: "application/xml" },
  });

  if (!data) {
    throw new Error("No XML content received");
  }

  triggerBrowserDownload(
    new Blob([data], { type: "application/xml;charset=utf-8" }),
    `${invoiceNumber}.xml`,
  );
};

// ---------------------------------------------------------------------------
// Batch upload
// ---------------------------------------------------------------------------

/**
 * Initialise a new invoice upload batch.
 * @param {object} data - Batch init payload
 * @returns {Promise<object>}
 */
export const initiateBatch = async (data) => {
  const { data: responseData } = await apiClient.post(
    "/invoice-uploads/batch/init",
    data,
  );
  return responseData;
};

/**
 * Fetch current batch upload status.
 * @returns {Promise<object>}
 */
export const getBatchStatus = async () => {
  const { data } = await apiClient.get("/invoice-uploads/batch/status");
  return data;
};

/**
 * Cancel (delete) an invoice upload from the processing queue.
 * Permanently deletes the file from storage and soft-deletes the record
 * (audit trail preserved). Only allowed before ZATCA submission.
 *
 * @param {string} recordId - InvoiceUpload record id
 * @param {boolean} [deleteFile=true] - also remove the file from GCS
 * @returns {Promise<object>}
 */
export const deleteUpload = async (recordId, deleteFile = true) => {
  const { data } = await apiClient.delete(
    `/invoice-uploads/${recordId}?deleteFile=${deleteFile}`,
  );
  return data;
};

// ---------------------------------------------------------------------------
// Invoice processing progress
// ---------------------------------------------------------------------------

/**
 * Persist current invoice processing progress to the backend.
 * Called on SSE "submitted" events and on page beforeunload.
 *
 * @param {{ invoices: object[] }} body
 * @returns {Promise<object>}
 */
export const saveInvoiceProgress = async (body) => {
  const { data } = await apiClient.post("/invoice-uploads/save-progress", body);
  return data;
};

/**
 * Restore invoice processing progress from the backend.
 * Called on mount of the invoice-processing-queue page.
 *
 * @returns {Promise<object>}
 */
export const getInvoiceProgress = async () => {
  const { data } = await apiClient.get("/invoice-uploads/get-progress");
  return data;
};

// ---------------------------------------------------------------------------
// Private DOM helpers
// ---------------------------------------------------------------------------

/**
 * Triggers a browser file download for a given Blob.
 * Prefixed with _ to signal it is not part of the public API surface.
 *
 * @param {Blob}   blob
 * @param {string} filename
 */
export const triggerBrowserDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
